/**
 * Agent Bridge — Exposes app internals on window.swan for AI agents
 *
 * Accessible via Chrome DevTools MCP or browser console.
 * Usage: window.swan.menuService.updateMenuItem('subjects', { parentId: 'school' })
 *        window.swan.diagnose() — app health snapshot
 */

import { db, auth } from '../firebase';
import { firestoreService } from '../firestoreService';
import menuService from '../services/menuService';
import schoolService from '../services/schoolService';
import UserService from '../services/UserService';
import { subjectsService } from '../services/subjectsService';
import { timetableAssignmentsService } from '../services/timetableAssignmentsService';
import { dayTemplatesService } from '../services/dayTemplatesService';
import { timetablesService } from '../services/timetablesService';
import { staffProfileService } from '../services/staffProfileService';
import consequenceTypesService from '../services/consequenceTypesService';
import { disciplineService } from '../services/disciplineService';
import ListenerManager from '../services/ListenerManager';
import { draftSessionService } from '../services/draftSessionService';
import useDraftModeStore from '../stores/draftModeStore';

const isAgentBridgeEnabled = () => (
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  window.localStorage.getItem('swan_enable_dev_tools') === 'true'
);

/**
 * Get current schoolId from localStorage (handles keyed format)
 */
const getSchoolId = () => {
  // Key format: swan_school_context_{uid}
  const key = Object.keys(localStorage).find(k => k.startsWith('swan_school_context_'));
  if (!key) return null;
  try {
    return JSON.parse(localStorage.getItem(key)).schoolId || null;
  } catch { return null; }
};

/**
 * Diagnose app health — returns a snapshot of runtime state
 */
const diagnose = () => {
  const schoolContext = localStorage.getItem('schoolContext');
  const menuCache = localStorage.getItem('swan_menu_items');
  const authCache = localStorage.getItem('authCache');

  const parsed = {
    school: schoolContext ? JSON.parse(schoolContext) : null,
    menu: menuCache ? JSON.parse(menuCache) : null,
    auth: authCache ? JSON.parse(authCache) : null,
  };

  const currentUser = auth.currentUser;

  return {
    user: currentUser ? {
      uid: currentUser.uid,
      email: currentUser.email,
      displayName: currentUser.displayName,
    } : null,
    school: parsed.school ? {
      schoolId: parsed.school.schoolId,
      schoolName: parsed.school.school?.name,
      permissions: parsed.school.permissions,
    } : null,
    menu: parsed.menu ? {
      itemCount: parsed.menu.flat?.length || 0,
      cacheAge: parsed.menu.timestamp
        ? `${((Date.now() - parsed.menu.timestamp) / 1000).toFixed(0)}s ago`
        : 'unknown',
      roles: Object.keys(parsed.menu.nested || {}),
    } : null,
    listeners: ListenerManager.getStatus?.() || 'unavailable',
    localStorage: {
      used: `${(JSON.stringify(localStorage).length / 1024).toFixed(1)}KB`,
      keys: Object.keys(localStorage).length,
    },
  };
};

// Register on window
window.swan = {
  // Firebase
  db,
  auth,
  firestoreService,

  // Core services
  menuService,
  schoolService,
  UserService,
  subjectsService,
  staffProfileService,

  // Timetable services
  timetableAssignmentsService,
  dayTemplatesService,
  timetablesService,

  // Discipline services
  disciplineService,
  consequenceTypesService,

  // Utilities
  ListenerManager,

  // Diagnostics
  diagnose,

  // Draft Mode — Agentic dry-run collaboration
  draftMode: {
    /**
     * Create a new draft session. Returns sessionId.
     * @param {string} type - e.g. 'timetableAssignments'
     * @param {object} context - e.g. { timetableId: '...', classId: '...' }
     */
    createSession: async (type, context = {}) => {
      const schoolId = getSchoolId();
      if (!schoolId) throw new Error('No school context — user must be logged in');

      const sessionId = await draftSessionService.create(schoolId, { type, context });

      // Also start the Zustand store so the React UI picks it up
      useDraftModeStore.getState().startSession(sessionId, type, context);

      console.log(`📝 Draft session created: ${sessionId}`);
      return sessionId;
    },

    /**
     * Write a single entry to the active draft session.
     * @param {string} sessionId
     * @param {string} entryId - deterministic slot id
     * @param {object} data - entry data
     */
    writeEntry: async (sessionId, entryId, data) => {
      const schoolId = getSchoolId();
      if (!schoolId) throw new Error('No school context');

      await draftSessionService.writeEntry(schoolId, sessionId, entryId, {
        ...data,
        _source: 'agent',
      });
    },

    /**
     * Write multiple entries at once.
     * @param {string} sessionId
     * @param {object} entriesMap - { entryId: data, ... }
     */
    writeEntries: async (sessionId, entriesMap) => {
      const schoolId = getSchoolId();
      if (!schoolId) throw new Error('No school context');

      // Tag all entries as agent-sourced
      const tagged = {};
      Object.entries(entriesMap).forEach(([id, data]) => {
        tagged[id] = { ...data, _source: 'agent' };
      });

      await draftSessionService.writeEntries(schoolId, sessionId, tagged);
    },

    /**
     * Update the agent progress display.
     * @param {string} sessionId
     * @param {object} progress - { status, currentStep, stepsCompleted, stepsTotal }
     */
    updateProgress: async (sessionId, progress) => {
      const schoolId = getSchoolId();
      if (!schoolId) throw new Error('No school context');

      await draftSessionService.updateProgress(schoolId, sessionId, progress);
    },

    /** Get the current draft store state (for agent inspection) */
    getState: () => useDraftModeStore.getState(),

    /** Generate a deterministic slot ID for timetable entries */
    generateSlotId: (data) => timetableAssignmentsService.generateSlotId(data),
  },

  // AI Keys — retrieve locally stored API keys
  ai: {
    /** Get the stored Gemini API key (or null if not set) */
    getGeminiKey: () => localStorage.getItem('swan_gemini_api_key') || null,

    /** Check if a Gemini key is available */
    hasGeminiKey: () => !!localStorage.getItem('swan_gemini_api_key'),
  },
};

if (isAgentBridgeEnabled()) {
  console.log('Agent bridge ready — window.swan');
}
